import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { trackEvent } from '@Utilities/marketingTags'
import { MOBILE } from '../CustomLanding.styled'
import { colors, promo } from '../content'

const START = new Date(promo.start).getTime()
const END = new Date(promo.end).getTime()

type Remaining = { days: number; hours: number; minutes: number; seconds: number }

const split = (ms: number): Remaining => ({
	days: Math.floor(ms / 86400000),
	hours: Math.floor(ms / 3600000) % 24,
	minutes: Math.floor(ms / 60000) % 60,
	seconds: Math.floor(ms / 1000) % 60,
})

const pad = (n: number) => String(n).padStart(2, '0')

/* TEMPORARY — the September 2026 term-package promotion. Revert the commit
   that added this to remove it.

   Rendered once in LandingPage above the isAuth fork, so guests and logged-in
   students both see it and StudentLanding stays untouched.

   Returns null outside the campaign window, so the banner appears on 3 Sep and
   disappears after Wednesday 16 Sep on its own — no deploy needed either side.

   The remaining time is recomputed from Date.now() on every tick rather than
   decrementing a counter: browsers throttle timers in background tabs, and over
   a 14-day campaign a decrementing counter drifts badly. Kept as its own
   component so the per-second re-render does not touch the rest of the page. */
const PromoCountdown: React.FC = () => {
	const navigate = useNavigate()
	const [now, setNow] = useState(() => Date.now())

	useEffect(() => {
		if (Date.now() >= END) return undefined
		const id = window.setInterval(() => setNow(Date.now()), 1000)
		return () => window.clearInterval(id)
	}, [])

	if (now < START || now >= END) return null

	const left = split(END - now)

	const handleClick = () => {
		trackEvent('CTAClick', { cta_text: 'promo_term_package', cta_location: 'promo_banner' })
		navigate(promo.path)
	}

	return (
		<Bar dir="rtl">
			<Copy>
				<Badge>{promo.badge}</Badge>
				<Headline>{promo.headline}</Headline>
				<Sub>{promo.sub}</Sub>
			</Copy>

			<Timer>
				<TimerLabel>{promo.countdownLabel}</TimerLabel>
				{/* dir="ltr" keeps the unit order stable: the digits are
				    LTR-strong and would otherwise reorder inside the RTL page */}
				<Units dir="ltr">
					<Unit>
						<UnitValue>{pad(left.days)}</UnitValue>
						<UnitLabel>{promo.units.days}</UnitLabel>
					</Unit>
					<Unit>
						<UnitValue>{pad(left.hours)}</UnitValue>
						<UnitLabel>{promo.units.hours}</UnitLabel>
					</Unit>
					<Unit>
						<UnitValue>{pad(left.minutes)}</UnitValue>
						<UnitLabel>{promo.units.minutes}</UnitLabel>
					</Unit>
					<Unit>
						<UnitValue accent>{pad(left.seconds)}</UnitValue>
						<UnitLabel>{promo.units.seconds}</UnitLabel>
					</Unit>
				</Units>
			</Timer>

			<Cta onClick={handleClick}>{promo.cta}</Cta>
		</Bar>
	)
}

export default PromoCountdown

const Bar = styled.div`
	background: ${colors.navy};
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
	flex-wrap: wrap;
	padding: 14px 24px;
	${MOBILE} {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
		padding: 14px 16px;
	}
`

const Copy = styled.div`
	min-width: 0;
`

const Badge = styled.span`
	display: inline-block;
	background: ${colors.amber};
	color: ${colors.navy};
	font-size: 11px;
	font-weight: 700;
	padding: 3px 10px;
	border-radius: 999px;
	margin-bottom: 6px;
`

const Headline = styled.div`
	color: ${colors.amber};
	font-size: 17px;
	font-weight: 700;
	line-height: 1.5;
	${MOBILE} {
		font-size: 15px;
	}
`

const Sub = styled.div`
	color: rgba(255, 255, 255, 0.78);
	font-size: 13px;
	line-height: 1.6;
	margin-top: 2px;
	${MOBILE} {
		font-size: 12px;
	}
`

const Timer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	${MOBILE} {
		flex-direction: column;
		align-items: stretch;
		gap: 6px;
	}
`

const TimerLabel = styled.span`
	color: rgba(255, 255, 255, 0.7);
	font-size: 12px;
	white-space: nowrap;
`

const Units = styled.div`
	display: flex;
	gap: 6px;
	${MOBILE} {
		gap: 5px;
	}
`

/* min-width: 0 on the flex items, same lesson as StickyBar: a flex child
   defaults to min-width:auto, refuses to shrink below its content, and pushes
   the document wider than a 375px viewport — which in RTL shifts the whole
   page sideways. */
const Unit = styled.div`
	flex: 0 0 auto;
	min-width: 44px;
	background: ${colors.navyDeep};
	border-radius: 8px;
	padding: 6px 9px;
	text-align: center;
	${MOBILE} {
		flex: 1 1 0;
		min-width: 0;
		padding: 6px 2px;
	}
`

const UnitValue = styled.div<{ accent?: boolean }>`
	color: ${props => (props.accent ? colors.amber : '#ffffff')};
	font-size: 17px;
	font-weight: 700;
	line-height: 1;
	font-variant-numeric: tabular-nums;
	${MOBILE} {
		font-size: 16px;
	}
`

const UnitLabel = styled.div`
	color: rgba(255, 255, 255, 0.55);
	font-size: 10px;
	margin-top: 2px;
`

const Cta = styled.button`
	flex: 0 0 auto;
	min-width: 0;
	background: ${colors.amber};
	color: ${colors.navy};
	font-family: inherit;
	font-size: 14px;
	font-weight: 700;
	padding: 10px 20px;
	border: none;
	border-radius: 999px;
	cursor: pointer;
	white-space: nowrap;
	${MOBILE} {
		width: 100%;
		padding: 11px 12px;
	}
`
